import React from "react";
import StatusBadge from "../StatusBadge";
import { num } from "../../lib/format";

const REASON_TEXT = {
  MARKET_DATA_STALE: "Market data stale",
  OUTSIDE_TRADING_HOURS: "Outside trading hours",
  BROKER_DISCONNECTED: "Broker disconnected",
  PINNED_CONTRACT_UNAVAILABLE: "Pinned contract unavailable",
  NEW_PAIR_UNPRICED: "New contract pair has no price",
  MARGIN_BUFFER: "Margin buffer not met",
  RECOVERY_PENDING: "Recovery pending",
};

const humanise = (r) => REASON_TEXT[r] || String(r).replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

/** Why a rollover is waiting, from the persisted rollover document only (no client maths). */
export function rolloverWaitReason(ro) {
  if (!ro?.reason) return null;
  if (ro.reason === "ROLLOVER_AVERAGE_NOT_IMPROVED") {
    const c = ro.reference_check || {};
    return `Rolled average ${num(c.new_average_rolled_spread, 2)} is not above the old average ${num(c.old_average_spread, 2)} — new reference not accepted. Entries stay blocked; exits and AUTO continue.`;
  }
  if (ro.reason === "SPREAD_BELOW_MINIMUM") {
    return `Candidate spread ${ro.last_candidate_spread != null ? num(ro.last_candidate_spread, 2) : "—"} · Minimum spread ${num(ro.min_spread, 2)}`;
  }
  return humanise(ro.reason);
}

/**
 * The rollover state of a trade, as one of the operator states:
 * AVAILABLE · ROLLING · WAITING · STOPPED · COMPLETE · ABORTED BY EXIT (or null: nothing to show).
 */
export function rolloverStatus(trade, { offered } = {}) {
  const ro = trade.rollover || {};
  const prog = `${ro.pairs_done || 0}/${ro.pairs_total || 0} · min ${num(ro.min_spread, 2)}`;
  switch (ro.state) {
    case "ROLLING": return { status: "ROLLING", label: `ROLLING ${prog}` };
    case "ROLL_PAUSED": return { status: "ROLL_WAITING", label: `ROLLOVER WAITING ${prog}`, detail: rolloverWaitReason(ro) };
    case "ROLL_STOPPED": return { status: "ROLL_STOPPED", label: `ROLLOVER STOPPED ${prog}`,
      detail: ["ROLLOVER_AVERAGE_NOT_IMPROVED", "AUTO_EXIT_CANCELLED"].includes(ro.reason) ? rolloverWaitReason(ro) : "Stopped by operator — Resume to continue" };
    case "ABORTED_BY_EXIT": return { status: "ROLL_ABORTED", label: "ROLLOVER ABORTED BY EXIT", detail: ro.aborted_reason ? humanise(ro.aborted_reason) : null };
    default: break;
  }
  const last = (trade.rollover_history || []).slice(-1)[0];
  if (last?.completed_at && trade.current_set != null && Number(last.to_set) === Number(trade.current_set)) {
    return { status: "ROLL_COMPLETE", label: `ROLLOVER COMPLETE · set ${last.to_set}`, detail: `${last.pairs_done ?? "—"} pairs moved` };
  }
  if (offered) return { status: "ROLL_AVAILABLE", label: `ROLLOVER AVAILABLE · ${trade.earlier_leg_dte}d` };
  return null;
}

export default function RolloverStatus({ trade, offered, testId, compact }) {
  const s = rolloverStatus(trade, { offered });
  if (!s) return null;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <StatusBadge status={s.status} label={s.label} testid={testId} data-testid={testId}
        title={s.detail || undefined} />
      {!compact && s.detail && (
        <span className="text-[11px] text-fg-secondary mono" data-testid={testId ? `${testId}-reason` : undefined}>{s.detail}</span>
      )}
    </span>
  );
}
